import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue";
const _hoisted_1 = { class: "flex h-full flex-col gap-5 overflow-y-auto p-4 pt-20 xl:p-8 xl:pt-8" };
const _hoisted_2 = { class: "flex flex-col gap-1" };
const _hoisted_3 = { class: "text-sm font-medium text-eorange-600" };
const _hoisted_4 = { class: "text-2xl font-bold text-eblack-900" };
const _hoisted_5 = { class: "flex flex-wrap items-center justify-between gap-3" };
const _hoisted_6 = { class: "inline-flex rounded-lg border border-ewhite-200 bg-white p-1" };
const _hoisted_7 = ["disabled"];
const _hoisted_8 = {
    key: 0,
    class: "flex flex-wrap items-center justify-between gap-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600"
};
const _hoisted_9 = {
    key: 1,
    class: "rounded-lg border border-ewhite-200 bg-white p-6 text-sm text-eblack-500"
};
const _hoisted_10 = {
    key: 2,
    class: "rounded-lg border border-ewhite-200 bg-white p-8 text-center"
};
const _hoisted_11 = { class: "text-sm font-medium text-eblack-800" };
const _hoisted_12 = { class: "mt-1 text-sm text-eblack-500" };
const _hoisted_13 = {
    key: 3,
    class: "flex flex-col gap-3"
};
const _hoisted_14 = { class: "overflow-hidden rounded-lg border border-ewhite-200 bg-white" };
const _hoisted_15 = ["onClick"];
const _hoisted_16 = { class: "flex flex-wrap items-center gap-2" };
const _hoisted_17 = { class: "text-sm font-semibold text-eblack-900" };
const _hoisted_18 = { class: "rounded-full bg-ewhite-100 px-2 py-0.5 text-xs text-eblack-500" };
const _hoisted_19 = { class: "mt-1 block text-sm text-eblack-600" };
const _hoisted_20 = { class: "mt-2 block text-xs text-eblack-400" };
const _hoisted_21 = ["onClick"];
const _hoisted_22 = ["disabled"];
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useNotificationStore } from "../stores";
import { resolveNotificationRoute } from "../services/notification-route-resolver";
const activeTabClass = "bg-eorange-500 text-white";
const inactiveTabClass = "text-eblack-600 hover:bg-ewhite-100";
export default /*@__PURE__*/ _defineComponent({
    __name: 'NotificationCenter',
    setup(__props) {
        const router = useRouter();
        const store = useNotificationStore();
        const { t, te, locale } = useI18n();
        const toast = useToast();
        const status = computed(() => store.activeStatus);
        onMounted(() => {
            store.refresh();
        });
        function setStatus(nextStatus) {
            store.fetchNotifications(nextStatus);
        }
        function retry() {
            store.fetchNotifications(status.value);
        }
        async function markAllRead() {
            try {
                await store.readAll();
            }
            catch {
                toast.info(t("notifications.center.mark_read_failed"));
            }
        }
        async function openNotification(notification) {
            if (notification.status === "unread") {
                try {
                    await store.markRead(notification.id);
                }
                catch {
                    toast.info(t("notifications.center.mark_read_failed"));
                }
            }
            const target = resolveNotificationRoute(notification);
            if (!target) {
                toast.info(t("notifications.center.resource_unavailable"));
                return;
            }
            try {
                await router.push(target);
            }
            catch {
                toast.info(t("notifications.center.resource_unavailable"));
            }
        }
        function labelForType(type) {
            if (type.startsWith("conversation."))
                return t("notifications.kinds.conversation");
            if (type.startsWith("booking."))
                return t("notifications.kinds.booking");
            if (type.startsWith("itinerary."))
                return t("notifications.kinds.itinerary");
            return t("notifications.kinds.system");
        }
        function canOpen(notification) {
            return resolveNotificationRoute(notification) !== null;
        }
        function notificationTitle(notification) {
            return localizedNotificationText(notification, "title", notification.title);
        }
        function notificationBody(notification) {
            return localizedNotificationText(notification, "body", notification.body || t("notifications.fallback_body"));
        }
        function localizedNotificationText(notification, field, fallback) {
            const key = notification.translation_key
                ? `${notification.translation_key}.${field}`
                : "";
            if (!key || !te(key))
                return fallback;
            return t(key, notification.translation_variables ?? {});
        }
        function formatDate(value) {
            return new Intl.DateTimeFormat(locale.value, {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(new Date(value));
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)("notifications.center.eyebrow")), 1),
                    _createElementVNode("h1", _hoisted_4, _toDisplayString(_unref(t)("notifications.center.title")), 1)
                ]),
                _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("button", {
                            type: "button",
                            class: _normalizeClass(["rounded-md px-3 py-1.5 text-sm font-medium", status.value === 'all' ? activeTabClass : inactiveTabClass]),
                            onClick: _cache[0] || (_cache[0] = ($event) => (setStatus('all')))
                        }, _toDisplayString(_unref(t)("notifications.center.all")), 3),
                        _createElementVNode("button", {
                            type: "button",
                            class: _normalizeClass(["rounded-md px-3 py-1.5 text-sm font-medium", status.value === 'unread' ? activeTabClass : inactiveTabClass]),
                            onClick: _cache[1] || (_cache[1] = ($event) => (setStatus('unread')))
                        }, _toDisplayString(_unref(t)("notifications.center.unread")), 3)
                    ]),
                    _createElementVNode("button", {
                        type: "button",
                        class: "inline-flex h-9 items-center rounded-lg bg-egreen-500 px-3 text-sm font-medium text-white disabled:opacity-60",
                        disabled: _unref(store).isUpdating || _unref(store).unreadCount === 0,
                        onClick: markAllRead
                    }, _toDisplayString(_unref(t)("notifications.center.mark_all_read")), 9, _hoisted_7)
                ]),
                (_unref(store).error)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createElementVNode("span", null, _toDisplayString(_unref(store).error), 1),
                        _createElementVNode("button", {
                            type: "button",
                            class: "rounded-md border border-red-200 px-2.5 py-1 font-medium text-red-700 hover:bg-red-100",
                            "data-test": "notification-retry",
                            onClick: retry
                        }, _toDisplayString(_unref(t)("notifications.center.retry")), 1)
                    ]))
                    : _createCommentVNode("", true),
                (_unref(store).isLoading)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(_unref(t)("notifications.center.loading")), 1))
                    : (_unref(store).visibleNotifications.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(t)("notifications.center.empty_title")), 1),
                            _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(t)("notifications.center.empty_body")), 1)
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createElementVNode("div", _hoisted_14, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(store).visibleNotifications, (notification) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: notification.id,
                                        class: _normalizeClass(["flex w-full items-start gap-3 border-b border-ewhite-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-ewhite-50", notification.status === 'unread' ? 'bg-eorange-50/50' : 'bg-white'])
                                    }, [
                                        _createElementVNode("span", {
                                            class: _normalizeClass(["mt-1 h-2.5 w-2.5 flex-none rounded-full", notification.status === 'unread' ? 'bg-eorange-500' : 'bg-transparent'])
                                        }, null, 2),
                                        _createElementVNode("button", {
                                            type: "button",
                                            class: "min-w-0 flex-1 text-left",
                                            onClick: ($event) => (openNotification(notification))
                                        }, [
                                            _createElementVNode("span", _hoisted_16, [
                                                _createElementVNode("span", _hoisted_17, _toDisplayString(notificationTitle(notification)), 1),
                                                _createElementVNode("span", _hoisted_18, _toDisplayString(labelForType(notification.type)), 1)
                                            ]),
                                            _createElementVNode("span", _hoisted_19, _toDisplayString(notificationBody(notification)), 1),
                                            _createElementVNode("span", _hoisted_20, _toDisplayString(formatDate(notification.occurred_at)), 1)
                                        ], 8, _hoisted_15),
                                        (canOpen(notification))
                                            ? (_openBlock(), _createElementBlock("button", {
                                                key: 0,
                                                type: "button",
                                                class: "text-sm font-medium text-eorange-600 hover:text-eorange-700",
                                                "data-test": "notification-open-cta",
                                                onClick: _withModifiers(($event) => (openNotification(notification)), ["stop"])
                                            }, _toDisplayString(_unref(t)("notifications.center.open")), 9, _hoisted_21))
                                            : _createCommentVNode("", true)
                                    ], 2));
                                }), 128))
                            ]),
                            (_unref(store).hasMore)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    type: "button",
                                    class: "mx-auto inline-flex h-9 items-center rounded-lg border border-ewhite-300 bg-white px-4 text-sm font-medium text-eblack-700 hover:bg-ewhite-50 disabled:opacity-60",
                                    "data-test": "notification-load-more",
                                    disabled: _unref(store).isLoadingMore,
                                    onClick: _cache[2] || (_cache[2] = ($event) => (_unref(store).loadMore()))
                                }, _toDisplayString(_unref(store).isLoadingMore
                                    ? _unref(t)("notifications.center.loading_more")
                                    : _unref(t)("notifications.center.load_more")), 9, _hoisted_22))
                                : _createCommentVNode("", true)
                        ]))
            ]));
        };
    }
});
